import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { groupCountsByTab, moduleKeysForTab } from '@/utils/notificationRouting';

// ---------------------------------------------------------------------------
// useNotificationSummary — okunmamış bildirimlerin modül bazlı TEK kaynağı.
//
// Hem sidebar sekme rozetleri hem bildirim panelindeki modül çipleri buradan
// beslenir; böylece iki yüzey asla farklı sayı göstermez.
//
// NOT: `/notifications/in-app/{id}` içindeki `unread_count` yalnız çekilen
// sayfayı (limit 30) sayar; rozet için yetersizdir. Bu hook, tüm okunmamışları
// veritabanında sayan `/bildirim/unread-summary` ucunu kullanır.
// ---------------------------------------------------------------------------

const DEFAULT_POLL_MS = 60000;

export function useNotificationSummary({ personnelId, pollMs = DEFAULT_POLL_MS } = {}) {
  const [byModule, setByModule] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!personnelId || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/bildirim/unread-summary`);
      if (res.data?.success) {
        setByModule(res.data.by_module || {});
        setTotal(res.data.total || 0);
      }
    } catch {
      // Sessiz geç: rozet ikincil bilgidir, hata toast'ı kullanıcıyı boğmasın.
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [personnelId]);

  useEffect(() => {
    if (!personnelId) {
      setByModule({});
      setTotal(0);
      return undefined;
    }
    refresh();
    const timer = setInterval(refresh, pollMs);
    return () => clearInterval(timer);
  }, [personnelId, pollMs, refresh]);

  const byTab = useMemo(() => groupCountsByTab(byModule), [byModule]);

  /**
   * Bir sekmeye ait okunmamış bildirimleri topluca okundu yapar.
   * Sayacı 0 olan sekme için istek atılmaz.
   */
  const markTabRead = useCallback(async (tabId) => {
    if (!personnelId || !tabId || !byTab[tabId]) return;
    const keys = moduleKeysForTab(tabId);
    if (keys.length === 0) return;
    try {
      await axios.post(`${API}/api/bildirim/mark-read-by-module`, { keys });
      await refresh();
    } catch {
      // Sessiz geç — bir sonraki poll'da sayaç yine doğru gelir.
    }
  }, [personnelId, byTab, refresh]);

  return { byModule, byTab, total, loading, refresh, markTabRead };
}

export default useNotificationSummary;
